import React, { useState } from 'react';
import { RiskAnalysisDTO } from '../../models/companyModels';
import { 
  getRiskScoreColor,
  getConfidenceColor,
  getImpactLevelColor,
  formatRiskScore,
  formatConfidence
} from '../../models/riskAnalysisModels';

const RiskAnalysisDetails = ({ riskAnalysis, className = '' }) => {
  const [expandedSections, setExpandedSections] = useState({
    keyFindings: true,
    individualRisks: false,
    recommendations: false,
    riskTrends: false
  });

  if (!riskAnalysis) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="text-center text-gray-500">
          <div className="text-4xl mb-2">📊</div>
          <p>No detailed risk analysis available</p>
        </div>
      </div>
    );
  }

  const analysis = new RiskAnalysisDTO(riskAnalysis);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getTopRiskScores = () => {
    return analysis.getTopRiskScores(5);
  };

  const getRiskScoresByCategory = () => {
    return analysis.getRiskScoresByCategory();
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Detailed Risk Analysis</h3>
        <p className="text-sm text-gray-500 mt-1">
          Comprehensive risk assessment for {analysis.companyName} ({analysis.ticker})
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Analysis Summary */}
        {analysis.analysisSummary && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Analysis Summary</h4>
            <p className="text-gray-600 leading-relaxed">{analysis.analysisSummary}</p>
          </div>
        )}

        {/* Key Findings */}
        {analysis.keyFindings && analysis.keyFindings.length > 0 && (
          <div>
            <button
              onClick={() => toggleSection('keyFindings')}
              className="flex items-center justify-between w-full text-left mb-3"
            >
              <h4 className="text-sm font-medium text-gray-700">Key Findings</h4>
              <svg
                className={`w-5 h-5 text-gray-400 transform transition-transform ${
                  expandedSections.keyFindings ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {expandedSections.keyFindings && (
              <ul className="space-y-2">
                {analysis.keyFindings.map((finding, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-600">{finding}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Individual Risk Scores */}
        {analysis.individualRiskScores && analysis.individualRiskScores.length > 0 && (
          <div>
            <button
              onClick={() => toggleSection('individualRisks')}
              className="flex items-center justify-between w-full text-left mb-3"
            >
              <h4 className="text-sm font-medium text-gray-700">Individual Risk Scores</h4>
              <svg
                className={`w-5 h-5 text-gray-400 transform transition-transform ${
                  expandedSections.individualRisks ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {expandedSections.individualRisks && (
              <div className="space-y-4">
                {getTopRiskScores().map((risk, index) => {
                  const scoreColor = risk.getScoreColor();
                  const impactColor = risk.getImpactLevelColor();
                  const confidenceColor = risk.getConfidenceColor();
                  
                  return (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-medium text-gray-900">{risk.riskName}</h5>
                        <div className="flex items-center space-x-3">
                          <span className={`text-sm font-semibold ${scoreColor.text}`}>
                            {formatRiskScore(risk.score)}/100
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${impactColor.bg} ${impactColor.text}`}>
                            {risk.impactLevel}
                          </span>
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-500">Risk Score</span>
                          <span className="text-xs text-gray-500">{formatRiskScore(risk.score)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${scoreColor.bg.replace('bg-', 'bg-').replace('-50', '')}`}
                            style={{ width: `${risk.score}%` }}
                          ></div>
                        </div>
                      </div>

                      {risk.reasoning && (
                        <p className="text-sm text-gray-600 mb-3">{risk.reasoning}</p>
                      )}

                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center">
                          <span className="mr-2">Confidence:</span>
                          <span className={`font-medium ${confidenceColor}`}>
                            {formatConfidence(risk.confidence)}%
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="mr-2">Category:</span>
                          <span className="font-medium capitalize">{risk.category.replace('_', ' ')}</span>
                        </div>
                      </div>

                      {risk.evidence && risk.evidence.length > 0 && (
                        <div className="mt-3">
                          <h6 className="text-xs font-medium text-gray-700 mb-2">Evidence:</h6>
                          <ul className="space-y-1">
                            {risk.evidence.map((evidence, evidenceIndex) => (
                              <li key={evidenceIndex} className="text-xs text-gray-600 flex items-start">
                                <span className="w-1 h-1 bg-gray-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                                {evidence}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {risk.mitigationSuggestions && risk.mitigationSuggestions.length > 0 && (
                        <div className="mt-3">
                          <h6 className="text-xs font-medium text-gray-700 mb-2">Mitigation Suggestions:</h6>
                          <ul className="space-y-1">
                            {risk.mitigationSuggestions.map((suggestion, suggestionIndex) => (
                              <li key={suggestionIndex} className="text-xs text-gray-600 flex items-start">
                                <span className="w-1 h-1 bg-green-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                                {suggestion}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Risk Trends */}
        {analysis.riskTrends && analysis.riskTrends.length > 0 && (
          <div>
            <button
              onClick={() => toggleSection('riskTrends')}
              className="flex items-center justify-between w-full text-left mb-3"
            >
              <h4 className="text-sm font-medium text-gray-700">Risk Trends</h4>
              <svg
                className={`w-5 h-5 text-gray-400 transform transition-transform ${
                  expandedSections.riskTrends ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {expandedSections.riskTrends && (
              <ul className="space-y-2">
                {analysis.riskTrends.map((trend, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-600">{trend}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Recommendations */}
        {analysis.recommendations && analysis.recommendations.length > 0 && (
          <div>
            <button
              onClick={() => toggleSection('recommendations')}
              className="flex items-center justify-between w-full text-left mb-3"
            >
              <h4 className="text-sm font-medium text-gray-700">Recommendations</h4>
              <svg
                className={`w-5 h-5 text-gray-400 transform transition-transform ${
                  expandedSections.recommendations ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {expandedSections.recommendations && (
              <ul className="space-y-2">
                {analysis.recommendations.map((recommendation, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-600">{recommendation}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskAnalysisDetails;
